import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import colors from 'resources/colors';
import { AnalysisData, StatisticalValueType } from 'models/analysis';
import { ChartHeaderPanel } from 'components/chart/ChartHeaderPanel';
import { LegendGroup, ChartLegendList } from '../common/ChartLegendList';
import { VesShipSpeedChart } from './VesShipSpeedChart';
import { ChartContainer, ChartContentsDiv, ChartDivider } from '../common/ChartContainer';
import msgId from 'resources/intl';
import dimens from 'resources/dimens';
import { useDispatch } from 'react-redux';
import {
  setVesShipSpeedExpanded,
  setVesShipSpeedStatisticalValueType,
  useAnalysisVesShipSpeedExpanded,
  useAnalysisVesShipSpeedStatisticalValueType,
} from 'features/contents/analysis/analysisSlice';
import { ConfirmedSearchResultItem } from 'models/search';

interface VesShipSpeedContainerProps {
  chartId: string;
  confirmedSearchResultItem: ConfirmedSearchResultItem;
  analysisData: AnalysisData;
  chartHeight: number;
}

export function VesShipSpeedContainer(props: VesShipSpeedContainerProps): JSX.Element {
  const { chartId, confirmedSearchResultItem, analysisData, chartHeight } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const expanded = useAnalysisVesShipSpeedExpanded();
  const statisticalValueType = useAnalysisVesShipSpeedStatisticalValueType();

  /**
   * CSV出力
   */
  const handleCsvOutput = () => {
    // noop
  };

  /**
   * 開閉状態変更
   * @param expanded 開閉状態
   */
  const handleExpandChanged = (expanded: boolean) => {
    dispatch(setVesShipSpeedExpanded(expanded));
  };

  /**
   * 分析値タイプ変更
   * @param statisticalValueType 分析値タイプ
   */
  const handleChangeStatisticalValueType = (statisticalValueType: StatisticalValueType) => {
    dispatch(setVesShipSpeedStatisticalValueType(statisticalValueType));
  };

  /**
   * 凡例リスト
   */
  let legendGroups: LegendGroup[];
  if (analysisData.dataFormatId !== 402) {
    legendGroups = [
      {
        groupName: analysisData.shipName + ' - ' + analysisData.machineName,
        legends: [
          {
            title: intl.formatMessage({ id: msgId.AnalysisMegBhpP1 }),
            color: colors.chart.lines[0],
            iconType: 'circle',
          },
          {
            title: intl.formatMessage({ id: msgId.AnalysisMegBhpS1 }),
            color: colors.chart.lines[1],
            iconType: 'circle',
          },
          {
            title: intl.formatMessage({ id: msgId.AnalysisMegSpdP1 }),
            color: colors.chart.lines[2],
            iconType: 'circle',
          },
          {
            title: intl.formatMessage({ id: msgId.AnalysisMegSpdS1 }),
            color: colors.chart.lines[3],
            iconType: 'circle',
          },
        ],
      },
    ];
  } else {
    legendGroups = [
      {
        groupName: analysisData.shipName + ' - ' + analysisData.machineName,
        legends: [
          {
            title: intl.formatMessage({ id: msgId.AnalysisMegBhpP1 }),
            color: colors.chart.lines[0],
            iconType: 'circle',
          },
          {
            title: intl.formatMessage({ id: msgId.AnalysisMegBhpS1 }),
            color: colors.chart.lines[1],
            iconType: 'circle',
          },
          {
            title: intl.formatMessage({ id: msgId.AnalysisEngSpdP }),
            color: colors.chart.lines[2],
            iconType: 'circle',
          },
          {
            title: intl.formatMessage({ id: msgId.AnalysisEngSpdS }),
            color: colors.chart.lines[3],
            iconType: 'circle',
          },
        ],
      },
    ];
  }

  const analysisName =
    intl.formatMessage({
      id: 'AnalysisSection' + analysisData.data.vesShipSpeed?.analysisSectionName,
    }) +
    ' : ' +
    intl.formatMessage({ id: 'AnalysisMenu' + analysisData.data.vesShipSpeed?.analysisMenuName });

  const chart = useMemo(
    () => (
      <VesShipSpeedChart
        sx={{ width: `calc(100% - ${dimens.chart.legend.width}px - 1px)` }}
        analysisData={analysisData}
        statisticalValueType={statisticalValueType}
        height={chartHeight}
      />
    ),
    [analysisData, statisticalValueType, chartHeight]
  );

  return (
    <ChartContainer
      chartId={chartId}
      shipName={analysisData.shipName}
      machineName={analysisData.machineName}
      analysisName={analysisName}
      confirmedSearchResultItem={confirmedSearchResultItem}
      title={analysisName}
      expanded={expanded}
      onExpandChanged={handleExpandChanged}
    >
      <ChartHeaderPanel
        chartId={chartId}
        sx={{ paddingRight: `${dimens.chart.legend.width}px` }}
        title={analysisName}
        titleEditable={false}
        csvOutputEnabled={false}
        csvOutputLocked={true}
      />
      <ChartContentsDiv>
        {chart}
        <ChartDivider orientation="vertical" flexItem />
        <ChartLegendList
          legendGroups={legendGroups}
          statisticalValueType={statisticalValueType}
          onChangeStatisticalValueType={handleChangeStatisticalValueType}
        />
      </ChartContentsDiv>
    </ChartContainer>
  );
}
